import { DataService } from "@/services/data";

import { ObjectListLayout2 } from "@/components/layout-2/object-list-2";
import { GenericDetailDynamicTabPage } from "@/components/layout/generic-detail-dynamic-tab-page";
import { GenericDetailLayout } from "@/components/layout/generic-detail-layout";
import AccessDenied from "@/pages/access-denied";
import ContactLMSLinkPage from "@/pages/contacts/lms-link";
import { MetadataService } from "@/services/metadata";
import { OptionsResponse } from "@/types/dynamic-detail";
import { buildObjectTitleFromTemplate } from "@/utils/template-title";
import { handleRelatedListAction } from "@/utils/related-list-action";
import { handle_error } from "@/utils/wretch";
import { legacyOfferHref } from "@/utils/apprenticeship-links";
import { redirect, RouteObject, useLoaderData, useParams } from "react-router-dom";
import { GenericDetailRelatedObject } from "@/components/layout/generic-detail-related-object";

type ObjectDetailLoaderData = {
    metadata: OptionsResponse;
    objectTitle?: string;
};

const SFLikeRoute: RouteObject = {
    path: ":objectName",
    children: [
        {
            id: "object-list",
            index: true,
            async lazy() {
                return {
                    Component: () => {
                        const { metadata } = useLoaderData() as { metadata: OptionsResponse };
                        if (!metadata) return <div>Error loading metadata</div>;
                        if (!metadata.is_access_allowed) return <AccessDenied />;
                        console.log("🏷️ Metadata", metadata);
                        return (
                            <ObjectListLayout2<{ id: number }>
                                metadata={metadata}
                                endpointUrl={`/crm/objects/${metadata.object_url}/`}
                                primary_column={metadata.list_config?.primary_column ?? "name"}
                                page_title={metadata?.model_name_plural ?? "Object"}
                                onRowNavigateTo={instance => {
                                    if (metadata.detail_base_url) {
                                        return `${metadata.detail_base_url}${instance.id}`;
                                    }
                                    return `/${metadata.object_url}/${instance.id}`;
                                }}
                                allowBatchSelection={true}
                                itemToId={instance => instance.id.toString()}
                            />
                        );
                    }
                };
            },
            loader: async ({ params }) => {
                // Hidden legacy offers surface: offers live on /opportunities.
                if (params.objectName === "apprenticeship-offers") return redirect("/opportunities");
                const metadata = await MetadataService.get(`/crm/objects/${params.objectName}/`);
                return { metadata };
            }
        },
        {
            id: "contacts-lms-link",
            path: "lms-link",
            async lazy() {
                return {
                    Component: () => {
                        const { objectName } = useParams();
                        // Cette route n'est active que pour les contacts
                        if (objectName !== "contacts") {
                            return <div>Not Found</div>;
                        }
                        return <ContactLMSLinkPage />;
                    }
                };
            }
        },
        {
            id: "object-detail",
            path: ":objectId",
            async lazy() {
                return {
                    Component: () => {
                        const { metadata, objectTitle } = useLoaderData() as ObjectDetailLoaderData;
                        const { objectName, objectId } = useParams();
                        const breadcrumbs = [{ name: metadata.model_name_plural ?? "", href: `/${objectName}` }, { name: objectTitle ?? "" }];
                        if (!metadata) return <div>Error loading metadata</div>;
                        return <GenericDetailLayout objectTitle={objectTitle} objectType={objectName ?? ""} endpointUrl={objectName} objectId={objectId ?? ""} breadcrumbs={breadcrumbs} />;
                    }
                };
            },
            loader: async ({ params }) => {
                const objectName = params.objectName;
                const objectId = params.objectId;
                if (objectName === "apprenticeship-offers" && objectId) return redirect(await legacyOfferHref(objectId));
                const object = (await DataService.client.url(`/crm/objects/${objectName}/${objectId}/`).get().json()) as { id: number; title?: string; subtitle?: string };
                const metadata = (await MetadataService.get(`/crm/objects/${objectName}/`)) as OptionsResponse;
                let objectTitle = object.title;
                if (!object.title && metadata?.detail_config?.object_detail_title && objectId) {
                    objectTitle = await buildObjectTitleFromTemplate(metadata.detail_config.object_detail_title, objectId, `/crm/objects/${objectName}/`);
                }
                return { object, metadata, objectTitle };
            },
            children: [
                {
                    path: ":tabPath/*",
                    loader: ({ params }) => {
                        if (params.objectName === "dialer-workflows" && params.tabPath === "versions") {
                            return redirect(`/dialer-workflows/${params.objectId}/informations`);
                        }
                        return null;
                    },
                    async lazy() {
                        return {
                            Component: () => {
                                const { objectName } = useParams();
                                return <GenericDetailDynamicTabPage objectType={objectName || ""} idParam="objectId" />;
                            }
                        };
                    },
                    action: async ({ request }) => {
                        const result = await handleRelatedListAction(request);
                        if (result !== null) return result;
                        return handle_error(new Error("Unknown action"));
                    }
                }
            ]
        },
        {
            id: "object-related",
            path: "related/:objectId/:relationshipName",
            async lazy() {
                return {
                    Component: () => {
                        const { metadata, objectTitle } = useLoaderData() as ObjectDetailLoaderData;
                        const { objectName, objectId, relationshipName } = useParams();
                        const breadcrumbs = [
                            { name: metadata.model_name_plural ?? "", href: `/${objectName}` },
                            { name: objectTitle ?? "", href: `/${objectName}/${objectId}` },
                            { name: `Related ${relationshipName}` }
                        ];
                        if (!metadata) return <div>Error loading metadata</div>;
                        return <GenericDetailRelatedObject objectType={objectName || ""} objectId={objectId || ""} breadcrumbs={breadcrumbs} />;
                    }
                };
            },
            loader: async ({ params }) => {
                const objectName = params.objectName;
                const objectId = params.objectId;
                if (objectName === "apprenticeship-offers" && objectId) return redirect(await legacyOfferHref(objectId));
                const object = (await DataService.client.url(`/crm/objects/${objectName}/${objectId}/`).get().json()) as { id: number; title?: string; subtitle?: string };
                const metadata = (await MetadataService.get(`/crm/objects/${objectName}/`)) as OptionsResponse;
                let objectTitle = object.title;
                if (!object.title && metadata?.detail_config?.object_detail_title && objectId) {
                    objectTitle = await buildObjectTitleFromTemplate(metadata.detail_config.object_detail_title, objectId, `/crm/objects/${objectName}/`);
                }
                return { object, metadata, objectTitle };
            }
        }
    ]
};

export default SFLikeRoute;
